import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&w=1600";
const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1600&q=80";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.25 } },
};
const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: EASE } },
};

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { t } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Server infrastructure"
          onError={(e) => {
            if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG;
          }}
          className="w-full h-[120%] object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/80 via-[#0B1120]/60 to-[#0B1120]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120] via-transparent to-[#0B1120]/40" />
      </motion.div>

      <div className="absolute top-1/3 -right-32 w-[480px] h-[480px] rounded-full bg-[#ED1C24]/15 blur-[140px] -z-10" />
      <div className="absolute bottom-0 -left-32 w-[380px] h-[380px] rounded-full bg-[#FFC61A]/8 blur-[120px] -z-10" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="px-6 md:px-12 lg:px-24 pt-32 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="inline-flex items-center gap-3 border-l-2 border-[#ED1C24] bg-white/5 backdrop-blur-sm px-5 py-2.5 mb-10"
        >
          <span
            data-testid="hero-badge"
            className="text-xs tracking-[0.25em] uppercase text-slate-200"
          >
            {t("hero_badge")}
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          data-testid="hero-headline"
          className="font-display font-semibold uppercase tracking-tight leading-[0.98] text-[9vw] sm:text-[7vw] lg:text-[4.6vw]"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineReveal} className="block">
              {t("hero_l1")}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineReveal} className="block text-[#ED1C24]">
              {t("hero_l2")}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineReveal} className="block">
              {t("hero_l3a")} <span className="text-stroke">{t("hero_l3b")}</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineReveal} className="block">
              {t("hero_l4a")} <span className="text-[#FFC61A]">{t("hero_l4b")}</span>
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-2xl text-slate-300 text-base lg:text-lg leading-relaxed"
        >
          {t("hero_sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-shop-btn"
            onClick={() => navigate("/shop")}
            className="group inline-flex items-center gap-3 rounded-full bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-4 transition-all duration-300 hover:shadow-[0_0_36px_rgba(237,28,36,0.5)] hover:scale-[1.03]"
          >
            {t("hero_shop")}
            <ArrowRight
              size={17}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </button>
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-3 rounded-full border border-white/25 hover:border-[#FFC61A] text-slate-100 hover:text-[#FFC61A] font-medium px-8 py-4 transition-colors duration-300"
          >
            {t("hero_quote")}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-3 max-w-2xl border-t border-white/10"
        >
          {[
            ["200+", t("stat_clients")],
            ["50+", t("stat_projects")],
            ["24/7", t("stat_support")],
          ].map(([num, label]) => (
            <div key={label} className="pt-5 pr-6">
              <div className="font-display text-3xl md:text-4xl text-white">
                {num}
              </div>
              <div className="text-xs tracking-[0.15em] uppercase text-slate-400 mt-1">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("about")}
        style={{ opacity: fade }}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 hover:text-white transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
}
